import IconPalette from "@shared/components/icons/Palette"
import IconTextColor from "@shared/components/icons/TextColor"
import type { CardColorType } from "@shared/models/card-stack"
import { OPERATION_CONTRACTS } from "@shared/unified-tiptap/analytics/operations"
import type { CardId } from "@shared/unified-tiptap/analytics/types"
import { TEXT_COLORS } from "@shared/unified-tiptap/extensions/color/color-extension"
import { getContainingCardId } from "@shared/unified-tiptap/util/card"
import { SECTION_COLOR_MAP } from "@shared/util/colors"
import type { Editor } from "@tiptap/core"
import { useEditorState } from "@tiptap/react"
import { CLEAR_SWATCH, pickerValue } from "./color-swatch-values"
import { ColorSplitControl, type SwatchItem } from "./ColorSplitControl"
import { useActiveMarkColor } from "./use-active-mark-color"

// Based on Tiptap UI's ColorTextPopover.

const TEXT_COLOR_OPTIONS = [{ name: "Default", color: null }, ...TEXT_COLORS]

/** The color the main button applies (text mode); the picker offers the full palette. */
const DEFAULT_TEXT_COLOR = TEXT_COLORS.find((entry) => entry.name === "Red")!.color

// cardTitle colors are a separate palette (SECTION_COLOR_MAP) from mark-based text colors,
// keyed by name, with black as the main-button default instead of an "unset".
const CARD_COLOR_OPTIONS = (Object.entries(SECTION_COLOR_MAP) as [CardColorType, string][]).map(
  ([key, color]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    color,
    key,
  }),
)

/**
 * When the selection is inside a cardTitle, we edit that card's color attr instead of the color
 * mark — so the branch resolves the card it would recolor, not just the fact that there is one.
 */
function selectedCardTitleCardId(editor: Editor): CardId | null {
  const { $from, $to } = editor.state.selection
  if ($from.parent.type.name !== "cardTitle" || $from.parent !== $to.parent) return null
  return getContainingCardId(editor.state.doc, $from.pos)
}

/** `null` clears the color mark; every other color sets it. */
function setTextColor(editor: Editor, color: string | null): void {
  const labeled = editor
    .chain()
    .label(OPERATION_CONTRACTS.OP_SET_TEXT_COLOR, "floating-toolbar", { color })
  if (color === null) labeled.unsetColor().run()
  else labeled.setColor(color).run()
}

function setCardColor(editor: Editor, cardId: CardId, color: CardColorType): void {
  editor
    .chain()
    .label(OPERATION_CONTRACTS.OP_SET_CARD_COLOR, "floating-toolbar", { color, cardId })
    .updateAttributes("cardTitle", { color })
    .run()
}

export function ColorPopover({ editor }: { editor: Editor }) {
  const cardId = useEditorState({ editor, selector: () => selectedCardTitleCardId(editor) })
  return cardId === null ? (
    <TextColorControl editor={editor} />
  ) : (
    <CardTitleColorControl editor={editor} cardId={cardId} />
  )
}

/** Body text: the `textStyle` color mark. `null` = default (unset), `undefined` = mixed. */
function TextColorControl({ editor }: { editor: Editor }) {
  const activeColor = useActiveMarkColor(editor, "textStyle")
  const canSet = useEditorState({
    editor,
    selector: (snapshot) => snapshot.editor.can().setColor("red"),
  })

  const items: SwatchItem[] = TEXT_COLOR_OPTIONS.map((entry) => ({
    color: entry.color ?? CLEAR_SWATCH,
    ariaLabel: `${entry.name} text`,
    glyph: <IconTextColor style={{ color: entry.color ?? undefined }} />,
    onSelect: () => setTextColor(editor, entry.color),
  }))

  return (
    <ColorSplitControl
      isDisabled={!canSet}
      isActive={!!activeColor}
      applyLabel="Text color"
      // The main button toggles the default color; the picker opens the full palette.
      onApply={() =>
        setTextColor(editor, activeColor === DEFAULT_TEXT_COLOR ? null : DEFAULT_TEXT_COLOR)
      }
      pickerLabel="Text color options"
      icon={<IconPalette activeColor={activeColor} />}
      items={items}
      selectedColor={pickerValue(activeColor)}
    />
  )
}

/** A cardTitle always has a color from its own palette (no "unset"); main button = black. */
function CardTitleColorControl({ editor, cardId }: { editor: Editor; cardId: CardId }) {
  const activeColor = useEditorState({
    editor,
    selector: (snapshot) =>
      SECTION_COLOR_MAP[snapshot.editor.getAttributes("cardTitle").color as CardColorType],
  })
  const canSet = useEditorState({
    editor,
    selector: (snapshot) => snapshot.editor.can().updateAttributes("cardTitle", { color: "red" }),
  })

  const items: SwatchItem[] = CARD_COLOR_OPTIONS.map((entry) => ({
    color: entry.color,
    ariaLabel: `${entry.name} card`,
    glyph: <IconTextColor style={{ color: entry.color }} />,
    onSelect: () => setCardColor(editor, cardId, entry.key),
  }))

  return (
    <ColorSplitControl
      isDisabled={!canSet}
      isActive={!!activeColor}
      applyLabel="Text color"
      onApply={() => setCardColor(editor, cardId, "black")}
      pickerLabel="Text color options"
      icon={<IconPalette activeColor={activeColor} />}
      items={items}
      selectedColor={pickerValue(activeColor)}
    />
  )
}
